import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';

const INACTIVITY_LIMIT_MS = 30 * 60 * 1000;  // 30 minutes
const WARNING_BEFORE_MS  =  2 * 60 * 1000;   //  2 minutes before logout

export function useInactivityTimeout(isAuthenticated) {
  const navigate = useNavigate();
  const timeoutRef    = useRef(null);
  const warningRef    = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(120);
  const countdownRef  = useRef(null);

  const clearAllTimers = useCallback(() => {
    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);
    clearInterval(countdownRef.current);
  }, []);

  const logout = useCallback(async () => {
    clearAllTimers();
    setShowWarning(false);
    await supabase.auth.signOut();
    navigate('/login?reason=timeout');
  }, [clearAllTimers, navigate]);

  const resetTimer = useCallback(() => {
    if (!isAuthenticated) return;
    clearAllTimers();
    setShowWarning(false);

    warningRef.current = setTimeout(() => {
      setShowWarning(true);
      setSecondsLeft(WARNING_BEFORE_MS / 1000);
      countdownRef.current = setInterval(() => {
        setSecondsLeft(s => {
          if (s <= 1) { clearInterval(countdownRef.current); return 0; }
          return s - 1;
        });
      }, 1000);
    }, INACTIVITY_LIMIT_MS - WARNING_BEFORE_MS);

    timeoutRef.current = setTimeout(logout, INACTIVITY_LIMIT_MS);
  }, [isAuthenticated, clearAllTimers, logout]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    const handler = () => resetTimer();

    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      clearAllTimers();
    };
  }, [isAuthenticated, resetTimer, clearAllTimers]);

  return { showWarning, secondsLeft, resetTimer, logout };
}

// ─── Warning Modal ────────────────────────────────────────────────────────────
export function InactivityWarningModal({ secondsLeft, onStayLoggedIn, onLogout }) {
  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const timeStr = mins > 0 ? `${mins}:${String(secs).padStart(2, '0')}` : `${secs}s`;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: '#161616', borderRadius: 16, padding: '2rem 1.75rem', maxWidth: 360, width: '100%', textAlign: 'center', border: '0.5px solid rgba(255,255,255,0.1)' }}>
        <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>⏱️</div>
        <h2 style={{ fontFamily: 'Syne, sans-serif', color: '#F5F0E8', fontSize: '1.3rem', fontWeight: 800, margin: '0 0 0.75rem' }}>Still there?</h2>
        <p style={{ color: 'rgba(245,240,232,0.55)', fontSize: '0.88rem', lineHeight: 1.6, margin: '0 0 1.5rem' }}>
          You've been inactive for a while. For your security, you'll be logged out in{' '}
          <span style={{ color: '#E8622A', fontWeight: 700, fontSize: '1rem' }}>{timeStr}</span>.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={onStayLoggedIn}
            style={{ flex: 1, padding: '0.75rem', borderRadius: 100, border: 'none', background: '#E8622A', color: '#fff', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
            Stay logged in
          </button>
          <button
            onClick={onLogout}
            style={{ flex: 1, padding: '0.75rem', borderRadius: 100, border: '0.5px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(245,240,232,0.5)', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
            Log out
          </button>
        </div>
      </div>
    </div>
  );
}
